import React, { useContext, useState, useEffect } from 'react';
import { firestore } from '../../firebase/config';
import { BookingContext } from '../../context/BookingContext';
import './ServiceSelection.css';

const ServiceSelection = ({ practitionerId, presetServiceId }) => {
  const { bookingData, updateBookingData, setCurrentStep, setSelectedService: setContextService } = useContext(BookingContext);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedServiceChanged, setSelectedServiceChanged] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier qu'un praticien est bien sélectionné
  useEffect(() => {
    if (!practitionerId) {
      console.warn("Aucun praticien sélectionné, redirection vers l'étape 1");
      setCurrentStep(1); // Rediriger vers l'étape 1 si aucun praticien sélectionné
    }
  }, [practitionerId, setCurrentStep]);

  // Charger les services du praticien sélectionné
  useEffect(() => {
    const fetchServices = async () => {
      if (!practitionerId) {
        setServices([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log("Chargement des services pour le praticien:", practitionerId);
        
        // Requête pour tous les services du praticien, actifs ou non
        const servicesSnapshot = await firestore
          .collection('services')
          .where('practitionerId', '==', practitionerId)
          .get();

        if (servicesSnapshot.empty) {
          console.warn("Aucun service trouvé pour ce praticien");
          setError("Aucun service disponible pour ce praticien.");
          setServices([]);
        } else {
          // Filtrer les services actifs côté client
          const allServices = servicesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          console.log("Services trouvés:", allServices);
          
          // Vérifier la présence du champ restTime dans chaque service
          allServices.forEach(service => {
            if (service.restTime === undefined || service.restTime === null) {
              console.warn(`Service ${service.id} (${service.title}) n'a pas de champ restTime défini.`);
            } else {
              console.log(`Service ${service.id} (${service.title}) a un temps de repos de ${service.restTime} minutes.`);
            }
          });
          
          // Filtrer les services actifs
          const activeServices = allServices.filter(service => 
            service.active !== false
          );
          
          if (activeServices.length === 0) {
            console.warn("Aucun service actif trouvé");
            setError("Aucun service actif disponible pour ce praticien.");
          }
          
          setServices(activeServices);
          
          // Si un service est présélectionné, le vérifier et le sélectionner
          if (presetServiceId && !selectedServiceChanged) {
            console.log("Recherche du service présélectionné:", presetServiceId);
            const presetService = activeServices.find(s => s.id === presetServiceId);
            
            if (presetService) {
              console.log("Service présélectionné trouvé:", presetService);
              setSelectedServiceId(presetService.id);
              
              // Si ce service n'est pas déjà dans le bookingData, l'enregistrer
              if (!bookingData.service || bookingData.service !== presetService.id) {
                console.log("Sélection du service présélectionné");
                handleServiceSelect(presetService);
              }
            } else {
              console.warn("Service présélectionné non trouvé ou inactif");
            }
          }
        }
      } catch (err) {
        console.error("Erreur lors du chargement des services:", err);
        setError("Impossible de charger les services du praticien.");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [practitionerId, presetServiceId, bookingData.service]);

  // Initialiser ou réinitialiser la sélection du service lorsque la liste des services change
  useEffect(() => {
    if (bookingData.service) {
      // Vérifier si le service précédemment sélectionné existe toujours dans la nouvelle liste
      const serviceExists = services.some(service => service.id === bookingData.service);
      
      if (!serviceExists) {
        console.warn("Le service sélectionné n'existe plus dans la liste");
        setSelectedServiceId(null);
      } else {
        setSelectedServiceId(bookingData.service);
      }
    } else {
      setSelectedServiceId(null);
    }
  }, [services, bookingData.service]);

  const handleServiceSelect = (service) => {
    if (!service || !service.id) {
      console.warn("Tentative de sélectionner un service invalide:", service);
      return;
    }
    
    console.log("Sélection du service:", service);
    console.log("Temps de repos du service:", service.restTime);
    
    setSelectedServiceId(service.id);
    
    // S'assurer que restTime est bien inclus et qu'il est traité comme un nombre
    const restTimeValue = service.restTime !== undefined && service.restTime !== null
      ? parseInt(service.restTime, 10)
      : 0;
    
    console.log("Temps de repos à transmettre:", restTimeValue, "type:", typeof restTimeValue);
    
    // Déterminer le prix standard et le prix à distance si disponible
    const standardPrice = service.price || 0;
    const remotePrice = service.remotePrice || standardPrice;
    
    // Utiliser la fonction provenant du contexte pour mettre à jour le service sélectionné
    // en incluant explicitement le temps de repos et les notes de service
    setContextService({
      id: service.id,
      title: service.title || 'Service sans titre',
      duration: service.duration || 60,
      price: standardPrice,
      remotePrice: remotePrice,
      restTime: restTimeValue,
      notes: service.notes || '',
      canBeRemote: service.canBeRemote || false,
      remoteOnly: service.remoteOnly || false,
    });
    
    console.log("Service transmis au contexte avec temps de repos:", restTimeValue);
    setSelectedServiceChanged(true);
  };

  const handleNext = () => {
    if (selectedServiceId) {
      console.log("Passage à l'étape 3 (calendrier)");
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    console.log("Retour à l'étape 1 (sélection praticien)");
    setCurrentStep(1);
  };

  if (loading) {
    return (
      <div className="service-selection loading-state">
        <div className="loading-spinner"></div>
        <p>Chargement des services...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="service-selection error-state">
        <div className="error-message">{error}</div>
        <button className="btn" onClick={handleBack}>
          Retour à la sélection du praticien
        </button>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="service-selection empty-state">
        <p>Aucun service disponible pour ce praticien.</p>
        <button className="btn" onClick={handleBack}>
          Retour à la sélection du praticien
        </button>
      </div>
    );
  }

  // Si nous avons un service présélectionné, ajouter une note d'information
  const hasPreselectedService = presetServiceId && selectedServiceId === presetServiceId;

  return (
    <div className="service-selection">
      <h2>Choisissez votre type de consultation</h2>
      
      {hasPreselectedService && (
        <div className="preselected-service-notice">
          <p>Un service a été présélectionné pour vous. Vous pouvez continuer avec ce service ou en choisir un autre.</p>
        </div>
      )}
      
      <div className="service-options">
        {services.map((service) => (
          <div
            key={service.id}
            className={`service-card ${selectedServiceId === service.id ? 'selected' : ''} ${service.remoteOnly ? 'remote-only-service' : ''}`}
            onClick={() => handleServiceSelect(service)}
          >
            <div className="service-title">{service.title}</div>
            <div className="service-duration">Durée: {service.duration} min</div>
            <div className="service-price">
              Tarif: {service.price}€
              {service.canBeRemote && service.remotePrice !== service.price && (
                <span className="remote-price-info"> (À distance: {service.remotePrice}€)</span>
              )}
            </div>
            
            {service.canBeRemote && (
              <div className="service-remote-badge">
                {service.remoteOnly ? 'À distance uniquement' : 'Disponible à distance'}
              </div>
            )}
            
            {service.description && (
              <div className="service-description">{service.description}</div>
            )}
          </div>
        ))}
      </div>
      
      <div className="step-navigation">
        <button className="btn btn-back" onClick={handleBack}>
          Retour
        </button>
        <button 
          className={`btn ${!selectedServiceId ? 'btn-disabled' : ''}`}
          onClick={handleNext}
          disabled={!selectedServiceId}
        >
          Suivant
        </button>
      </div>
    </div>
  );
};

export default ServiceSelection;